import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { toast } from 'sonner';
import GalleryPicker from './GalleryPicker';
import { 
  Upload, 
  FileText, 
  Music, 
  BookOpen, 
  Check, 
  AlertCircle, 
  Loader2, 
  Link as LinkIcon,
  RefreshCw,
  ExternalLink,
  Trash2,
  X,
  Box,
  Cloud,
  Copy
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Función para convertir links de Box a URL de descarga directa
const convertBoxLink = (boxUrl) => {
  // Formato típico: https://app.box.com/s/abc123xyz
  // Convertir a: https://app.box.com/shared/static/abc123xyz.ext
  const match = boxUrl.match(/box\.com\/s\/([a-zA-Z0-9]+)/);
  if (match) {
    return {
      success: true,
      directUrl: `https://app.box.com/shared/static/${match[1]}`,
      note: 'Agrega la extensión del archivo al final (.epub, .mp3, .pdf)'
    };
  }
  return { success: false, error: 'Link de Box no válido' };
};

// Función para convertir links de Google Drive
const convertGoogleDriveLink = (driveUrl) => {
  // Formato: https://drive.google.com/file/d/FILE_ID/view
  // Convertir a: https://drive.google.com/uc?export=download&id=FILE_ID
  const match = driveUrl.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (match) {
    return {
      success: true,
      directUrl: `https://drive.google.com/uc?export=download&id=${match[1]}`
    };
  }
  // Formato alternativo: https://drive.google.com/open?id=FILE_ID
  const match2 = driveUrl.match(/drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/);
  if (match2) {
    return {
      success: true,
      directUrl: `https://drive.google.com/uc?export=download&id=${match2[1]}`
    };
  }
  return { success: false, error: 'Link de Google Drive no válido' };
};

// Función para convertir links de Dropbox
const convertDropboxLink = (dropboxUrl) => {
  // Cambiar dl=0 por dl=1 o agregar raw=1
  if (dropboxUrl.includes('dropbox.com')) {
    let directUrl = dropboxUrl.replace('www.dropbox.com', 'dl.dropboxusercontent.com');
    directUrl = directUrl.replace('?dl=0', '?dl=1');
    if (!directUrl.includes('dl=1') && !directUrl.includes('raw=1')) {
      directUrl += directUrl.includes('?') ? '&raw=1' : '?raw=1';
    }
    return { success: true, directUrl };
  }
  return { success: false, error: 'Link de Dropbox no válido' };
};

const PublicalaUploader = ({ user, onClose }) => {
  const [mode, setMode] = useState('url'); // 'url' or 'file' (file disabled for now)
  const [loading, setLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [existingContent, setExistingContent] = useState([]);
  const [loadingContent, setLoadingContent] = useState(false);
  const [activeTab, setActiveTab] = useState('upload'); // 'upload' or 'library'
  const [showLinkConverter, setShowLinkConverter] = useState(false);
  const [converterInput, setConverterInput] = useState('');
  const [convertedUrl, setConvertedUrl] = useState(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [showCoverGallery, setShowCoverGallery] = useState(false);

  const handleCoverUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error('La imagen debe ser menor a 8MB'); return; }
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) { toast.error('Usa JPG, PNG, GIF o WEBP'); return; }
    setUploadingCover(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('image', file);
      const res = await axios.post(`${API}/api/imgtool/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      if (res.data?.url) {
        setFormData((prev) => ({ ...prev, cover_url: res.data.url }));
        toast.success('Portada subida a ImgTool');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir la portada');
    } finally {
      setUploadingCover(false);
      e.target.value = '';
    }
  };
  
  // Form fields
  const [formData, setFormData] = useState({
    name: '',
    external_id: '',
    publication_date: new Date().toISOString().split('T')[0],
    description: '',
    lang: 'es',
    author: user?.full_name || '',
    publisher: 'ViubuxConnect',
    keywords: '',
    price_mxn: '',
    price_usd: '',
    free: false,
    cover_url: '',
    file_url: '',
    extension: 'epub'
  });

  useEffect(() => {
    checkConnection();
    fetchExistingContent();
  }, []);

  const checkConnection = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/publicala/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConnectionStatus(response.data);
    } catch (error) {
      setConnectionStatus({ connected: false, error: error.message });
    }
  };

  const fetchExistingContent = async () => {
    setLoadingContent(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/publicala/content?page=1`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setExistingContent(response.data.items || []);
      }
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoadingContent(false);
    }
  };

  const handleConvertLink = () => {
    if (!converterInput.trim()) {
      toast.error('Pega un link para convertir');
      return;
    }

    let result = null;
    
    if (converterInput.includes('box.com')) {
      result = convertBoxLink(converterInput);
      result.source = 'Box';
    } else if (converterInput.includes('drive.google.com')) {
      result = convertGoogleDriveLink(converterInput);
      result.source = 'Google Drive';
    } else if (converterInput.includes('dropbox.com')) {
      result = convertDropboxLink(converterInput);
      result.source = 'Dropbox';
    } else {
      toast.error('Link no reconocido. Usa Box, Google Drive o Dropbox.');
      return;
    }

    if (result.success) {
      setConvertedUrl(result);
      toast.success(`Link de ${result.source} convertido`);
    } else {
      toast.error(result.error);
    }
  };

  const useConvertedUrl = () => {
    if (convertedUrl?.directUrl) {
      setFormData({ ...formData, file_url: convertedUrl.directUrl });
      setShowLinkConverter(false);
      setConverterInput('');
      setConvertedUrl(null);
      toast.success('URL aplicada al formulario');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado al portapapeles');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.file_url) {
      toast.error('Proporciona la URL del archivo');
      return;
    }
    
    if (!formData.name || !formData.external_id) {
      toast.error('Completa los campos requeridos');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const submitData = new FormData();
      submitData.append('file_url', formData.file_url);
      submitData.append('extension', formData.extension);
      submitData.append('name', formData.name);
      submitData.append('external_id', formData.external_id);
      submitData.append('publication_date', formData.publication_date);
      submitData.append('description', formData.description || '');
      submitData.append('lang', formData.lang);
      submitData.append('author', formData.author);
      submitData.append('publisher', formData.publisher);
      submitData.append('keywords', formData.keywords);
      submitData.append('free', formData.free);
      
      if (formData.price_mxn) {
        submitData.append('price_mxn', parseFloat(formData.price_mxn));
      }
      if (formData.price_usd) {
        submitData.append('price_usd', parseFloat(formData.price_usd));
      }
      if (formData.cover_url) {
        submitData.append('cover_url', formData.cover_url);
      }

      const response = await axios.post(
        `${API}/api/publicala/upload-by-url`,
        submitData,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        toast.success('¡Contenido enviado a Reader.Viubux.com!');
        toast.info(response.data.publicala_message || 'El contenido está siendo procesado');
        
        // Reset form
        setFormData({
          ...formData,
          name: '',
          external_id: '',
          description: '',
          file_url: '',
          cover_url: ''
        });
        
        // Refresh content list
        fetchExistingContent();
        setActiveTab('library');
      }
    } catch (error) {
      console.error('Upload error:', error);
      const errorMsg = error.response?.data?.detail || error.message;
      toast.error(`Error: ${typeof errorMsg === 'object' ? JSON.stringify(errorMsg) : errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const getFileTypeIcon = (type) => {
    switch (type) {
      case 'audio': return <Music className="w-4 h-4 text-purple-400" />;
      case 'pdf': return <FileText className="w-4 h-4 text-red-400" />;
      case 'epub': return <BookOpen className="w-4 h-4 text-blue-400" />;
      default: return <FileText className="w-4 h-4 text-gray-400" />;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-purple-500/30 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="bg-gradient-to-br from-purple-500 to-pink-500 p-3 rounded-xl">
              <Upload className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Publicar en Reader.Viubux.com</h2>
              <p className="text-white/60 text-sm">Sube ePUBs, audiolibros y PDFs directamente</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white p-2">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Connection Status */}
        <div className="px-6 py-3 bg-black/30 border-b border-white/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {connectionStatus?.connected ? (
                <>
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-green-400 text-sm">Conectado a {connectionStatus.domain}</span>
                </>
              ) : (
                <>
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  <span className="text-red-400 text-sm">Desconectado</span>
                </>
              )}
            </div>
            <button 
              onClick={checkConnection}
              className="text-white/60 hover:text-white text-sm flex items-center gap-1"
            >
              <RefreshCw className="w-3 h-3" /> Verificar
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10">
          <button
            onClick={() => setActiveTab('upload')}
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'upload'
                ? 'text-purple-400 border-b-2 border-purple-500 bg-purple-500/10'
                : 'text-white/60 hover:text-white'
            }`}
          >
            <Upload className="w-4 h-4 inline mr-2" />
            Subir Contenido
          </button>
          <button
            onClick={() => setActiveTab('library')}
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'library'
                ? 'text-purple-400 border-b-2 border-purple-500 bg-purple-500/10'
                : 'text-white/60 hover:text-white'
            }`}
          >
            <BookOpen className="w-4 h-4 inline mr-2" />
            Explorar últimas publicaciones ({existingContent.length})
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'upload' ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* File Type Selection */}
              <div>
                <label className="block text-white/70 text-sm mb-2">Tipo de Archivo *</label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { id: 'epub', label: 'eBook (EPUB)', icon: BookOpen, color: 'blue' },
                    { id: 'mp3', label: 'Audiolibro (MP3)', icon: Music, color: 'purple' },
                    { id: 'pdf', label: 'PDF', icon: FileText, color: 'red' }
                  ].map(type => (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setFormData({ ...formData, extension: type.id })}
                      className={`p-4 rounded-xl border transition-all flex flex-col items-center gap-2 ${
                        formData.extension === type.id
                          ? `bg-${type.color}-500/20 border-${type.color}-500 text-${type.color}-400`
                          : 'bg-black/30 border-white/10 text-white/60 hover:border-white/30'
                      }`}
                    >
                      <type.icon className="w-6 h-6" />
                      <span className="text-sm">{type.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* File URL */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-white/70 text-sm flex items-center gap-1">
                    <LinkIcon className="w-4 h-4" />
                    URL del Archivo *
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowLinkConverter(true)}
                    className="text-xs bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-blue-400 px-3 py-1 rounded-full border border-blue-500/30 hover:border-blue-500/50 transition-colors flex items-center gap-1"
                  >
                    <Cloud className="w-3 h-3" />
                    Convertir Link (Box, Drive, Dropbox)
                  </button>
                </div>
                <input
                  type="url"
                  value={formData.file_url}
                  onChange={(e) => setFormData({ ...formData, file_url: e.target.value })}
                  className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                  placeholder="https://app.box.com/... o URL directa al archivo"
                  required
                />
                <div className="flex items-center gap-4 mt-2">
                  <div className="flex items-center gap-2 text-white/40 text-xs">
                    <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-500/10 rounded">
                      <Box className="w-3 h-3" /> Box
                    </span>
                    <span className="flex items-center gap-1 px-2 py-0.5 bg-green-500/10 rounded">
                      <Cloud className="w-3 h-3" /> Drive
                    </span>
                    <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-500/10 rounded">
                      <Cloud className="w-3 h-3" /> Dropbox
                    </span>
                  </div>
                </div>
                <p className="text-white/50 text-xs mt-3 leading-relaxed" data-testid="file-url-help">
                  Comparte el URL del Drive en la nube donde tienes almacenado tu contenido. Si no tienes algún servidor en la nube, te recomendamos abrir una cuenta gratuita en Box en{' '}
                  <a href="https://vbx.viubux.com/box" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline">vbx.viubux.com/box</a>{' '}
                  primero. Cuando subas tus archivos ahí, busca la opción de "compartir" y ese enlace lo copias aquí.
                </p>
              </div>

              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/70 text-sm mb-2">Título *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                    placeholder="Mi Audiolibro"
                    required
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">ISBN / ID Externo *</label>
                  <input
                    type="text"
                    value={formData.external_id}
                    onChange={(e) => setFormData({ ...formData, external_id: e.target.value })}
                    className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white font-mono focus:outline-none focus:border-purple-500"
                    placeholder="978-123-456-789"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/70 text-sm mb-2">Fecha de Publicación</label>
                  <input
                    type="date"
                    value={formData.publication_date}
                    onChange={(e) => setFormData({ ...formData, publication_date: e.target.value })}
                    className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">Idioma</label>
                  <select
                    value={formData.lang}
                    onChange={(e) => setFormData({ ...formData, lang: e.target.value })}
                    className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                  >
                    <option value="es">Español</option>
                    <option value="en">Inglés</option>
                    <option value="pt">Portugués</option>
                    <option value="fr">Francés</option>
                  </select>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-white/70 text-sm mb-2">Descripción</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500 h-24 resize-none"
                  placeholder="Sinopsis o descripción del contenido..."
                />
              </div>

              {/* Author & Publisher */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/70 text-sm mb-2">Autor(es)</label>
                  <input
                    type="text"
                    value={formData.author}
                    onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                    className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                    placeholder="Nombre del autor"
                  />
                  <p className="text-white/40 text-xs mt-1">Separa múltiples autores con comas</p>
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">Editorial</label>
                  <input
                    type="text"
                    value={formData.publisher}
                    onChange={(e) => setFormData({ ...formData, publisher: e.target.value })}
                    className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                    placeholder="ViubuxConnect"
                  />
                </div>
              </div>

              {/* Cover URL */}
              <div>
                <label className="block text-white/70 text-sm mb-2">Portada (opcional)</label>
                <div className="flex items-start gap-3">
                  {formData.cover_url && (
                    <img src={formData.cover_url} alt="Portada" className="w-16 h-20 object-cover rounded-lg border border-purple-500/30 flex-shrink-0" data-testid="cover-preview" />
                  )}
                  <div className="flex-1 space-y-2">
                    <input
                      type="url"
                      value={formData.cover_url}
                      onChange={(e) => setFormData({ ...formData, cover_url: e.target.value })}
                      className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                      placeholder="https://... (JPG o PNG)"
                    />
                    <label className="inline-flex items-center gap-2 text-xs bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-blue-300 px-3 py-1.5 rounded-full border border-blue-500/30 hover:border-blue-500/50 transition-colors cursor-pointer" data-testid="cover-imgtool-btn">
                      {uploadingCover ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                      {uploadingCover ? 'Subiendo...' : 'Subir imagen (ImgTool)'}
                      <input type="file" accept="image/*" className="hidden" disabled={uploadingCover} onChange={handleCoverUpload} data-testid="cover-imgtool-input" />
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowCoverGallery(true)}
                      className="inline-flex items-center gap-2 text-xs text-purple-300 px-3 py-1.5 rounded-full border border-purple-500/30 hover:border-purple-500/50 transition-colors ml-2"
                      data-testid="cover-gallery-btn"
                    >
                      <BookOpen className="w-3.5 h-3.5" /> Elegir de mi galería
                    </button>
                    <p className="text-white/40 text-xs">Sube tu portada y se aloja en Viubux ImgTool, o pega una URL.</p>
                  </div>
                </div>
              </div>

              <GalleryPicker
                open={showCoverGallery}
                onClose={() => setShowCoverGallery(false)}
                onSelect={(url) => setFormData((prev) => ({ ...prev, cover_url: url }))}
              />

              {/* Pricing */}
              <div className="bg-black/20 rounded-xl p-4 border border-white/10">
                <div className="flex items-center justify-between mb-4">
                  <label className="text-white/70 text-sm">Precios</label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.free}
                      onChange={(e) => setFormData({ ...formData, free: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-white/60 text-sm">Contenido gratuito</span>
                  </label>
                </div>
                
                {!formData.free && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white/50 text-xs mb-1">Precio MXN</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.price_mxn}
                        onChange={(e) => setFormData({ ...formData, price_mxn: e.target.value })}
                        className="w-full bg-black/30 border border-white/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-purple-500"
                        placeholder="199.00"
                      />
                    </div>
                    <div>
                      <label className="block text-white/50 text-xs mb-1">Precio USD</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.price_usd}
                        onChange={(e) => setFormData({ ...formData, price_usd: e.target.value })}
                        className="w-full bg-black/30 border border-white/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-purple-500"
                        placeholder="9.99"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={loading || !connectionStatus?.connected}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-4 rounded-xl font-semibold"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Publicando...
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5 mr-2" />
                    Publicar en Reader.Viubux.com
                  </>
                )}
              </Button>
            </form>
          ) : (
            /* Library Tab */
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold">Contenido Publicado</h3>
                <button
                  onClick={fetchExistingContent}
                  className="text-purple-400 hover:text-purple-300 text-sm flex items-center gap-1"
                >
                  <RefreshCw className={`w-4 h-4 ${loadingContent ? 'animate-spin' : ''}`} />
                  Actualizar
                </button>
              </div>

              {loadingContent ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
                </div>
              ) : existingContent.length === 0 ? (
                <div className="text-center py-12">
                  <BookOpen className="w-12 h-12 text-white/20 mx-auto mb-4" />
                  <p className="text-white/60">No hay contenido publicado aún</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {existingContent.map(item => (
                    <div
                      key={item.id}
                      className="bg-black/30 border border-white/10 rounded-xl p-4 flex items-center gap-4 hover:border-purple-500/30 transition-colors"
                    >
                      {item.cover ? (
                        <img
                          src={item.cover}
                          alt={item.name}
                          className="w-16 h-20 object-cover rounded-lg"
                        />
                      ) : (
                        <div className="w-16 h-20 bg-gray-800 rounded-lg flex items-center justify-center">
                          {getFileTypeIcon(item.file_type)}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="text-white font-medium truncate">{item.name}</h4>
                        <p className="text-white/50 text-sm font-mono">{item.external_id}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="flex items-center gap-1 text-xs text-white/40">
                            {getFileTypeIcon(item.file_type)}
                            {item.file_type}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            item.status === 'done' 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {item.status === 'done' ? 'Publicado' : 'Procesando'}
                          </span>
                        </div>
                      </div>
                      <a
                        href={`https://app.viubux.com/library/publication/${item.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-purple-400 hover:text-purple-300 p-2"
                      >
                        <ExternalLink className="w-5 h-5" />
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Link Converter Modal */}
      {showLinkConverter && (
        <div className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-purple-500/30 rounded-2xl w-full max-w-lg overflow-hidden">
            {/* Header */}
            <div className="p-5 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-gradient-to-br from-blue-500 to-purple-500 p-2 rounded-lg">
                  <Cloud className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Convertir Link de Nube</h3>
                  <p className="text-white/50 text-xs">Box, Google Drive, Dropbox</p>
                </div>
              </div>
              <button 
                onClick={() => {
                  setShowLinkConverter(false);
                  setConverterInput('');
                  setConvertedUrl(null);
                }}
                className="text-white/60 hover:text-white p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-5 space-y-4">
              {/* Supported Services */}
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-center">
                  <div className="bg-blue-500/20 w-10 h-10 rounded-lg mx-auto flex items-center justify-center mb-2">
                    <Box className="w-5 h-5 text-blue-400" />
                  </div>
                  <span className="text-blue-400 text-xs font-medium">Box</span>
                </div>
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-center">
                  <div className="bg-green-500/20 w-10 h-10 rounded-lg mx-auto flex items-center justify-center mb-2">
                    <Cloud className="w-5 h-5 text-green-400" />
                  </div>
                  <span className="text-green-400 text-xs font-medium">Google Drive</span>
                </div>
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-center">
                  <div className="bg-blue-500/20 w-10 h-10 rounded-lg mx-auto flex items-center justify-center mb-2">
                    <Cloud className="w-5 h-5 text-blue-300" />
                  </div>
                  <span className="text-blue-300 text-xs font-medium">Dropbox</span>
                </div>
              </div>

              {/* Input */}
              <div>
                <label className="block text-white/70 text-sm mb-2">Pega el link compartido</label>
                <input
                  type="url"
                  value={converterInput}
                  onChange={(e) => setConverterInput(e.target.value)}
                  className="w-full bg-black/30 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                  placeholder="https://app.box.com/s/... o https://drive.google.com/..."
                />
              </div>

              {/* Convert Button */}
              <Button
                type="button"
                onClick={handleConvertLink}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Convertir a URL Directa
              </Button>

              {/* Result */}
              {convertedUrl && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-2 text-green-400 text-sm">
                    <Check className="w-4 h-4" />
                    <span>Link de {convertedUrl.source} convertido</span>
                  </div>
                  
                  <div className="bg-black/30 rounded-lg p-3">
                    <p className="text-white/50 text-xs mb-1">URL de descarga directa:</p>
                    <div className="flex items-center gap-2">
                      <code className="text-green-300 text-xs break-all flex-1">
                        {convertedUrl.directUrl}
                      </code>
                      <button
                        onClick={() => copyToClipboard(convertedUrl.directUrl)}
                        className="text-white/60 hover:text-white p-1"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {convertedUrl.note && (
                    <p className="text-yellow-400/80 text-xs flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {convertedUrl.note}
                    </p>
                  )}

                  <Button
                    type="button"
                    onClick={useConvertedUrl}
                    className="w-full bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Usar esta URL
                  </Button>
                </div>
              )}

              {/* Instructions */}
              <div className="bg-black/20 rounded-lg p-4 border border-white/5">
                <p className="text-white/50 text-xs font-medium mb-2">Instrucciones:</p>
                <ul className="text-white/40 text-xs space-y-1">
                  <li>• <strong className="text-blue-400">Box:</strong> Comparte el archivo y copia el link</li>
                  <li>• <strong className="text-green-400">Google Drive:</strong> Clic derecho → "Obtener enlace"</li>
                  <li>• <strong className="text-blue-300">Dropbox:</strong> Comparte → Copiar enlace</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicalaUploader;
